import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import FailureThumb from "../../assets/icons/svg/failure.svg";

interface ProgressiveImageProps {
  src: string;
  placeholder?: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  onClick?: () => void;
  width?: number | string;
  height?: number | string;
  useProgressiveLoading?: boolean; // New flag to control progressive loading
}

export const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  placeholder,
  alt = "",
  className = "",
  style = {},
  onLoad,
  onError,
  onClick,
  width,
  height,
  useProgressiveLoading = true,
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder || src);
  const [isLoading, setIsLoading] = useState(placeholder !== src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (src === imageSrc && !isLoading) return;
    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    img.onload = () => {
      setImageSrc(src);
      setIsLoading(false);
      onLoad?.();
    };
    img.onerror = () => {
      setHasError(true);
      setIsLoading(false);
      onError?.();
    };
    img.src = src;
  }, [src, imageSrc, onLoad, onError]);

  if (!useProgressiveLoading) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        onLoad={onLoad}
        onError={onError}
      />
    );
  }

  return (
    <div
      className={`progressive-image-container ${className}`}
      style={{
        position: "relative",
        width: width || "auto",
        height: height || "auto",
        ...style,
        objectFit: "contain",
      }}
      onClick={onClick}
    >
      {isLoading && (
        <div
          className="progressive-image-placeholder"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
          }}
        >
          <Loader2 className="animate-spin" size={24} />
        </div>
      )}
      {hasError && (
        <div
          className="progressive-image-error"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
            color: "#6c757d",
            borderRadius: "4px",
          }}
        >
          <img
            src={FailureThumb}
            alt="Image failed to load"
            className="w-100 h-100 object-fit-cover"
          />
        </div>
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={`progressive-image ${isLoading ? "loading" : "loaded"}`}
        style={{
          width: "100%",
          height: "100%",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
          borderRadius: "4px",
          ...style,
          objectFit: "contain",
        }}
      />
    </div>
  );
};

export default ProgressiveImage;
